import { z } from 'zod';
import { commaSeparated } from '../common/comma-separated';
import { ListSearchTermSchema } from '../common/search-term.schema';
import { ActorTypeSchema, type ActorType } from '../common/actor.schema';
import { TRANSACTION_TYPES, TransactionTypeSchema, type TransactionType } from '../common/transaction-type.schema';
import { LeadSourceSchema } from '../common/lead-source.schema';
import { DealLinksSchema } from '../common/links.schemas';
import { refersTo } from '../common/refers-to';
import { CommissionStatementSchema } from '../property/buyer-commission';
import { MandateCommissionPercentSchema, MandateDurationMonthsSchema, MandateTypeSchema } from './mandate-terms';

// Domain enum tokens are en-US per ADR 0009 (2026-05-21 amendment). The
// German labels users see (IMM-9: `Im Austausch`, `Aktiv`, `Anderer Makler`,
// …) live in `packages/i18n` and are looked up by these en-US keys, so
// adding a value here forces a translation update in every locale.

// IMM-9 nine-phase ordered flow. Array order IS the pipeline order
// (`IN_EXCHANGE` first, `ONLINE` last) — forward/backward transitions in
// later slices derive direction from the index.
/**
 * The single attention signal a manager reads off a deal (IMM-430.6). Exactly one
 * applies, by precedence OVERDUE > NO_NEXT_STEP > TOO_LONG_IN_PHASE > ON_TRACK,
 * and only ever for an ACTIVE deal — a held, draft or closed deal is not stalled,
 * so it carries no flag at all.
 *
 * `ON_TRACK` is the ABSENCE of the other three, never a per-phase evaluation.
 *
 * Anglicised from the slice's pinned German tokens: German belongs in `@immo/i18n`
 * values, not in identifiers, as IMM-430.1/.4/.5 already settled.
 */
export const DEAL_ATTENTION_FLAGS = ['OVERDUE', 'NO_NEXT_STEP', 'TOO_LONG_IN_PHASE', 'ON_TRACK'] as const;
export const DealAttentionFlagSchema = z.enum(DEAL_ATTENTION_FLAGS);
export type DealAttentionFlag = z.infer<typeof DealAttentionFlagSchema>;

export const DEAL_PHASES = [
  'IN_EXCHANGE',
  'APPOINTMENT_SCHEDULED',
  'APPOINTMENT_COMPLETED',
  'VALUATION_COMPLETED',
  'MANDATE_RECEIVED',
  'CONTRACT_SENT',
  'CONTRACT_RECEIVED',
  'EXPOSE_REVIEW',
  'ONLINE',
] as const;

// DRAFT (IMM-213) reserves a property the moment an Owner-Lead is qualified,
// before the deal is worked. It sits ahead of ACTIVE and is hidden from the
// default Active deal list (surfaced under an explicit status filter), just as
// IN_ACQUISITION properties are hidden from the default property list.
export const DEAL_STATUSES = ['DRAFT', 'ACTIVE', 'DEFERRED', 'WON', 'LOST'] as const;

// The reserving (non-terminal) statuses: a deal in one of these holds its
// property + type slot (the `deals_one_active_per_property_type` partial index)
// and can still be marked verloren. One shared source so the FE action gate and
// backend queries cannot drift apart again (IMM-429 — the mark-lost gate once
// enumerated these inline and silently missed DRAFT).
export const RESERVING_DEAL_STATUSES = ['DRAFT', 'ACTIVE', 'DEFERRED'] as const;

export const DEAL_SOURCES = ['MANUAL', 'LEAD'] as const;

export const DEAL_PROBABILITIES = ['LOW', 'MEDIUM', 'HIGH'] as const;

export const DEAL_COMPLETION_HORIZONS = [
  'UNDER_ONE_MONTH',
  'ONE_TO_THREE_MONTHS',
  'THREE_TO_SIX_MONTHS',
  'OVER_SIX_MONTHS',
] as const;

// IMM-9 fixed thirteen-item Verlustgrund list — mandatory when a Deal is
// marked LOST (enforced in a later slice).
export const DEAL_LOSS_REASONS = [
  'OTHER_BROKER',
  'OWNER_NOT_SELLING',
  'OWNER_UNREACHABLE',
  'UNREALISTIC_PRICE',
  'OWNER_SELLS_PRIVATE',
  'WRONG_CONTACT_TYPE',
  'OWNER_UNDECIDED',
  'PROPERTY_NOT_SELLABLE',
  'SALE_UNECONOMICAL',
  'OWNER_NO_RESPONSE',
  'OWNER_ALREADY_KNOWN',
  'NO_AGREEMENT',
  'OTHER',
] as const;

// Direction of a phase transition in the append-only Phasen-Historie.
// Null on the initial entry written at creation.
export const DEAL_PHASE_DIRECTIONS = ['FORWARD', 'BACKWARD'] as const;

export const DealPhaseSchema = z.enum(DEAL_PHASES);
export const DealStatusSchema = z.enum(DEAL_STATUSES);
export const DealSourceSchema = z.enum(DEAL_SOURCES);
export const DealProbabilitySchema = z.enum(DEAL_PROBABILITIES);
export const DealCompletionHorizonSchema = z.enum(DEAL_COMPLETION_HORIZONS);
export const DealLossReasonSchema = z.enum(DEAL_LOSS_REASONS);
export const DealPhaseDirectionSchema = z.enum(DEAL_PHASE_DIRECTIONS);

// A deal's transaction type is the shared SALE / RENTAL axis; it must match
// the linked property's marketing type (enforced in the create handler).
export const DEAL_TYPES = TRANSACTION_TYPES;
export const DealTypeSchema = TransactionTypeSchema;

export type DealPhase = z.infer<typeof DealPhaseSchema>;
export type DealStatus = z.infer<typeof DealStatusSchema>;
export type DealSource = z.infer<typeof DealSourceSchema>;
export type DealProbability = z.infer<typeof DealProbabilitySchema>;
export type DealCompletionHorizon = z.infer<typeof DealCompletionHorizonSchema>;
export type DealLossReason = z.infer<typeof DealLossReasonSchema>;
export type DealPhaseDirection = z.infer<typeof DealPhaseDirectionSchema>;
export type DealType = TransactionType;

// The `numeric(14, 2)` column's ceiling; a larger price would overflow in Postgres as a 500.
const MAX_EXPECTED_SALE_PRICE = 999_999_999_999.99;

export const CreateDealPathParamsSchema = z.object({
  organizationId: z.uuid(),
});

const DEPRECATED_BUYER_COMMISSION =
  'Deprecated (IMM-1380): ignored on write. The buyer commission belongs to the Objekt — set it with PUT /v1/organizations/{organizationId}/properties/{id}/pricing-sale.';

export const CreateDealBodySchema = z.object({
  propertyId: z.uuid(),
  type: DealTypeSchema,
  // Owner contacts, ordered; index 0 is the primary Kontakt. At least one.
  contactIds: z.array(z.uuid()).min(1),
  expectedSalePrice: z.number().nonnegative().max(MAX_EXPECTED_SALE_PRICE).nullish(),
  // The seller's commission (IMM-1150). Omitted = the Objekt's default (IMM-1192, `defaultCommissionFor`).
  commissionPercent: z.number().min(0).max(100).optional(),
  // Deprecated (IMM-1380): accepted and ignored. The Objekt owns the buyer commission.
  buyerCommissionPercent: z.number().min(0).max(100).nullable().optional().meta({
    deprecated: true,
    description: DEPRECATED_BUYER_COMMISSION,
  }),
  probability: DealProbabilitySchema.nullish(),
  completionHorizon: DealCompletionHorizonSchema.nullish(),
  votAgreed: z.boolean().optional(),
  // Defaults to MANUAL; IMM-8's lead-conversion path supplies LEAD + the lead id.
  source: DealSourceSchema.optional(),
  sourceLeadId: z.uuid().nullish(),
  // Optional free-text Notizen captured at creation; each seeds one row in the
  // `deal_obstacles` / `deal_next_steps` timelines.
  obstacles: z.string().trim().max(1000).optional(),
  nextSteps: z.string().trim().max(1000).optional(),
});

// The source Lead's acquisition context, carried on the Deal detail read
// (IMM-456.6). A snapshot resolved at request time rather than columns copied
// onto the deal at conversion, so a later correction to the Lead's Notizen is
// reflected here instead of going stale. `concern` is the Lead's own Notizen —
// what the owner actually asked for — normalised to null when blank.
export const DealOriginSchema = z.object({
  source: LeadSourceSchema,
  receivedAt: z.iso.datetime(),
  // Null when the qualifying member has since been removed from the workspace, or
  // when the Lead was never converted — the card drops the name, not the row.
  qualifiedByDisplayName: z.string().nullable(),
  concern: z.string().nullable(),
});

export type DealOrigin = z.infer<typeof DealOriginSchema>;

export const DealResponseSchema = z.object({
  id: z.uuid(),
  version: z.number().int().positive(),
  workspaceId: z.uuid(),
  organizationId: refersTo('organization', z.uuid()),
  propertyId: refersTo('property', z.uuid()),
  type: DealTypeSchema,
  contactIds: z.array(refersTo('contact', z.uuid())).min(1),
  phase: DealPhaseSchema,
  status: DealStatusSchema,
  source: DealSourceSchema,
  sourceLeadId: refersTo('lead', z.uuid()).nullable(),
  createdByActorId: refersTo('member', z.uuid()),
  expectedSalePrice: z.number().nullable(),
  // IMM-1150: the commission per side, in percent of the sale price, net of VAT. Null = not recorded.
  sellerCommissionPercent: z.number().nullable(),
  // IMM-1380: the buyer side is the Objekt's (`property_pricing_sale`), never stored on the Deal.
  buyerCommissionPercent: z.number().nullable().meta({
    description:
      'The Objekt’s buyer commission as a net rate in percent; 0 when käuferprovisionsfrei, null for a fixed amount or while it is missing.',
  }),
  buyerCommission: CommissionStatementSchema.nullable(),
  buyerCommissionAmountNet: z.number().nullable(),
  // IMM-1380.2: the Auftrag's own buyer percent from before the Objekt owned it, when it differs from the Objekt's. Null once the member picked.
  buyerCommissionDifference: z.object({ mandatePercent: z.number() }).nullable(),
  // Kept, unchanged in meaning for every deal that existed before IMM-1150, and still populated:
  // it is on the published contract (`apps/api-docs/public-api-surface.txt`).
  commissionPercent: z.number().meta({
    deprecated: true,
    description:
      'Deprecated — use `sellerCommissionPercent` and `buyerCommissionPercent`. The seller’s commission in percent; 0 when none is recorded. `expectedCommission` includes the buyer’s commission too.',
  }),
  // Server-derived (`expectedSalePrice × (sellerCommissionPercent + buyerCommissionPercent) / 100
  // + buyerCommissionAmountNet`, an empty side counting as 0), null when no price is set — the SAME derivation and nullability as `DealListItemSchema`.
  // Carried on the detail read (IMM-456.2) so the deal header's „Erwartete
  // Maklergebühr" cannot disagree with the figure the list row shows.
  expectedCommission: z.number().nullable(),
  // IMM-1150: the Auftrag terms. Response types are looser than the body validators (BL-039).
  mandateType: MandateTypeSchema.nullable(),
  mandateStartDate: z.iso.date().nullable(),
  mandateDurationMonths: z.number().int().nullable(),
  // Derived by `mandateEndsOn`; null while start or duration is empty. Never stored.
  mandateEndsOn: z.iso.date().nullable(),
  probability: DealProbabilitySchema.nullable(),
  completionHorizon: DealCompletionHorizonSchema.nullable(),
  votAgreed: z.boolean(),
  lossReason: DealLossReasonSchema.nullable(),
  // Free-text note captured alongside the Verlustgrund when a deal is marked
  // LOST; null when none was given or the deal was never lost.
  lossNote: z.string().nullable(),
  // When the deal was marked LOST (the LOST status-change `occurred_at`); null
  // while it is not lost. Cleared on reopen.
  lostAt: z.iso.datetime().nullable(),
  // When the agent clicked `[Behalten]` on the post-Verloren cleanup banner
  // (IMM-171.3) — null while never dismissed. The FE hides the banner while
  // `cleanupDismissedAt >= lostAt`; a reopen → re-lost bumps `lostAt` past it,
  // re-offering the cleanup without any reset on reopen.
  cleanupDismissedAt: z.iso.datetime().nullable(),
  deferredUntil: z.string().nullable(),
  // `Zurückgestellt seit` — when the deal was last put on hold (the latest
  // DEFERRED status-change `occurred_at`); null while it is not deferred. The
  // FE freezes `Tage in Phase` against this instant while the deal is on hold.
  deferredSince: z.iso.datetime().nullable(),
  // When the deal entered its current phase — the latest `deal_phase_history`
  // row, the same expression the list row derives (IMM-456.2). Non-nullable:
  // every deal has an initial phase row, and the read falls back to `createdAt`
  // rather than omitting the field. Backs the header's „Tag N in Phase", frozen
  // against `deferredSince` while the deal is on hold.
  phaseEnteredAt: z.iso.datetime(),
  // Latest free-text Notizen from the `deal_obstacles` / `deal_next_steps`
  // timelines; null when none recorded. Read-only mirror of the create-form
  // fields — edits land through the (future) timeline endpoints, not here.
  obstacles: z.string().nullable(),
  nextSteps: z.string().nullable(),
  // The deal's live responsible-member set (IMM-402), id + display name, for the
  // „Verantwortliche Mitglieder" display and the „Zuweisen" dialog's pre-selection.
  // Empty on responses that don't resolve it (e.g. mutations that re-fetch after).
  responsibleMembers: z.array(z.object({ memberId: refersTo('member', z.uuid()), displayName: z.string() })),
  // IMM-456.6 — read-only snapshot of the qualified Lead this Deal came from, so
  // the acquisition story survives conversion. Resolved on the two DETAIL reads
  // only: it is a cross-module lead read, and no write handler holds that port, so
  // every mutation response carries `null` here exactly as `responsibleMembers`
  // carries `[]`. Also `null` for a MANUAL deal (no `sourceLeadId`) and for a deal
  // whose source lead has since been deleted — neither is an error.
  origin: DealOriginSchema.nullable(),
  // Superseded by `links.self` (IMM-569.8), which names the route that actually
  // produced the response — this field is always the ORGANISATION-addressed URL,
  // even on the workspace-addressed detail read. Kept, unchanged in value, and
  // still populated: it is on a published contract, so removing it would break
  // partners for no gain. Marked deprecated so the announcement half of the
  // announce-then-remove path is visible in the reference and in the surface file.
  detailUrl: z.string().meta({
    deprecated: true,
    description:
      'Deprecated — use `links.self`. Always the organisation-addressed detail URL of this deal, regardless of which detail route returned it. Still populated and still correct; `links.self` is the URL of the endpoint you actually called.',
  }),
  // IMM-569.8 — the deal's own URL plus the URLs of its property, activity
  // timeline, phase history and valuation request. Additive and never
  // load-bearing. `self` echoes the route the caller used: a deal is published
  // under two detail routes, and each returns its own, so two responses carrying
  // different `self` values may still be the same deal. A link says the resource
  // exists, never that this key may read it.
  links: DealLinksSchema,
  // Both fields as on `DealListItemSchema` — see there (IMM-430.6).
  attentionFlag: DealAttentionFlagSchema.nullable(),
  activeDaysInPhase: z.number().int().min(0),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime().nullable(),
});

export const CreateDealResponseSchema = DealResponseSchema;

export const GetDealPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

export const GetDealResponseSchema = DealResponseSchema;

// Workspace-addressed detail read (IMM-20.4): a deal surfaced on the board may
// live in a Standort other than the caller's selected one, so the detail fetch
// is keyed by workspace + id rather than organisation + id.
export const GetWorkspaceDealPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  id: z.uuid(),
});

// Slim list-row shape. The cross-module Kontakt / Objekt names are NOT joined
// here — the FE hydrates them from the carried `contactIds` / `propertyId`
// (the list query stays inside the deal module's tables). `contactIds` is the
// full ordered owner roster (index 0 = primary); the FE derives the "(+ N)"
// owner count and matches the Kontakt search against every linked owner from it.
// `expectedCommission` is server-derived (`expectedSalePrice × (sellerCommissionPercent +
// buyerCommissionPercent) / 100 + buyerCommissionAmountNet`, an empty side counting as 0; null when no price is set); `phaseEnteredAt` backs the "days in phase"
// the FE renders and the default sort (longest in phase first).
export const DealListItemSchema = z.object({
  id: z.uuid(),
  // The deal's owning Standort — carried so a consumer can resolve the linked
  // property / contacts through the org-scoped reads (IMM-348).
  organizationId: refersTo('organization', z.uuid()),
  propertyId: refersTo('property', z.uuid()),
  type: DealTypeSchema,
  contactIds: z.array(refersTo('contact', z.uuid())).min(1),
  phase: DealPhaseSchema,
  status: DealStatusSchema,
  // How the deal was created (IMM-876.2): `LEAD` when it came from qualifying a
  // lead, `MANUAL` otherwise. The same value `DealResponse.source` carries.
  source: DealSourceSchema,
  // The lead this deal was qualified from; null exactly when `source` is MANUAL
  // (enforced by `deals_source_lead_id_check`). Not a foreign key — the lead may
  // since have been deleted.
  sourceLeadId: refersTo('lead', z.uuid()).nullable(),
  createdByActorId: refersTo('member', z.uuid()),
  expectedSalePrice: z.number().nullable(),
  // IMM-1150: the commission per side, in percent of the sale price, net of VAT. Null = not recorded.
  sellerCommissionPercent: z.number().nullable(),
  // IMM-1380: the buyer side is the Objekt's (`property_pricing_sale`), never stored on the Deal.
  buyerCommissionPercent: z.number().nullable().meta({
    description:
      'The Objekt’s buyer commission as a net rate in percent; 0 when käuferprovisionsfrei, null for a fixed amount or while it is missing.',
  }),
  buyerCommission: CommissionStatementSchema.nullable(),
  buyerCommissionAmountNet: z.number().nullable(),
  // IMM-1380.2: the Auftrag's own buyer percent from before the Objekt owned it, when it differs from the Objekt's. Null once the member picked.
  buyerCommissionDifference: z.object({ mandatePercent: z.number() }).nullable(),
  // Kept, unchanged in meaning for every deal that existed before IMM-1150, and still populated:
  // it is on the published contract (`apps/api-docs/public-api-surface.txt`).
  commissionPercent: z.number().meta({
    deprecated: true,
    description:
      'Deprecated — use `sellerCommissionPercent` and `buyerCommissionPercent`. The seller’s commission in percent; 0 when none is recorded. `expectedCommission` includes the buyer’s commission too.',
  }),
  expectedCommission: z.number().nullable(),
  probability: DealProbabilitySchema.nullable(),
  completionHorizon: DealCompletionHorizonSchema.nullable(),
  // The follow-up date (`Zurückgestellt bis`) on a deferred deal; null otherwise.
  // Backs the date shown on the Kanban card in the `Zurückgestellt` lane.
  deferredUntil: z.string().nullable(),
  // `Zurückgestellt seit` — when the deal was last put on hold (the latest
  // DEFERRED status-change `occurred_at`); null while it is not deferred. The
  // FE freezes `Tage in Phase` against this instant while the deal is on hold.
  deferredSince: z.iso.datetime().nullable(),
  phaseEnteredAt: z.iso.datetime(),
  // The deal's live responsible-member id set (IMM-402), `[]` when orphaned. A
  // slim id list (like `contactIds`) — the pipeline surfaces (402.6) hydrate the
  // display names; the resolved names live on the detail `DealResponse`.
  responsibleMemberIds: z.array(refersTo('member', z.uuid())),
  // The member whose deactivation last emptied the responsible set (IMM-402.6);
  // null when the deal has never been orphaned by a deactivation. Backs the
  // „Früher verantwortlich" column in the orphan-queue view — the consumer
  // hydrates the display name from the workspace member roster.
  formerResponsibleMemberId: refersTo('member', z.uuid()).nullable(),
  detailUrl: z.string(),
  // The derived attention signal (IMM-430.6). Derived at read time from the
  // deal's open activities and its time in phase, never stored — the same rule
  // the „Überfällig" badge already follows. `null` whenever the deal is not
  // ACTIVE.
  attentionFlag: DealAttentionFlagSchema.nullable(),
  // „Tage in Phase" with every interval spent Zurückgestellt subtracted, so a
  // deal that was held does not read as stalled on the day it resumes. Reported
  // for EVERY deal — the board shows the counter on held and closed deals too;
  // only the flag above is ACTIVE-only.
  activeDaysInPhase: z.number().int().min(0),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type DealListItem = z.infer<typeof DealListItemSchema>;

export const ListDealsPathParamsSchema = z.object({
  organizationId: z.uuid(),
});

// Filters answerable from the deal module's own tables. Kontakt / Objekt
// free-text search is deliberately omitted here (slim projection — see
// `DealListItemSchema`); it lands once the query bus exposes the owning
// modules' read models. `mine` scopes to deals the caller created.
export const ListDealsQuerySchema = z.object({
  status: DealStatusSchema.optional(),
  phase: DealPhaseSchema.optional(),
  type: DealTypeSchema.optional(),
  mine: z.stringbool().optional(),
  // „Mir zugewiesen" (IMM-402.4): keep only deals whose live responsible set
  // contains the caller. Server-resolved from the caller's member id — it never
  // carries a client-supplied id. `orphaned` keeps only deals with an EMPTY
  // responsible set (the „Verwaiste Deals" queue); it is gated on the org-scoped
  // `organization:deal:deal-assign` by the orphan-queue-access middleware.
  assignedToMe: z.stringbool().optional(),
  orphaned: z.stringbool().optional(),
  // Widens a non-DRAFT `status` read (the default `ACTIVE` working board) to also
  // surface the DRAFT placeholders a Qualify reserves (IMM-213), so just-qualified
  // deals show alongside the active pipeline. Mirrors the leads inbox
  // `includeArchived` widening; ignored when `status` is absent or already DRAFT.
  includeDrafts: z.stringbool().optional(),
});

export const ListDealsResponseSchema = z.object({
  data: z.array(DealListItemSchema),
});

export type ListDealsPathParams = z.infer<typeof ListDealsPathParamsSchema>;
export type ListDealsQuery = z.infer<typeof ListDealsQuerySchema>;
export type ListDealsResponse = z.infer<typeof ListDealsResponseSchema>;

// Workspace-addressed deals list (IMM-20.4 hierarchical Standort visibility).
// Scopes rows to the caller's visible Standort set rather than a single
// organisation; the response is the SAME slim list DTO as the org-addressed
// list. `organizationId` optionally narrows the result to one Standort within
// that visible set (the org-switch picker) — it is intersected with, never
// widens, what the caller may already see.
export const ListWorkspaceDealsPathParamsSchema = z.object({
  workspaceId: z.uuid(),
});

export const ListWorkspaceDealsQuerySchema = ListDealsQuerySchema.extend({
  // Optional free-text filter (IMM-448.4). A `deals` row holds no searchable
  // text, so the term is resolved exactly the way the ⌘K palette's Deals source
  // resolves it: the property module answers which Objekte the term matched
  // (`PropertySearchLookup`), and the deal module keeps the deals hanging off
  // them under its own visible-Standort predicate. Absent or blank → no search.
  q: ListSearchTermSchema,
  organizationId: z.uuid().optional(),
  // Narrow to this set of Standorte (a node + its descendants), intersected
  // with the caller's visible set. The frontend computes the subtree id set
  // from the org tree it holds and sends it `?organizationIds=uuid,uuid`. Takes
  // precedence over the single `organizationId` when non-empty; ids outside the
  // visible set are ignored (never widens). Absent / blank → no narrowing.
  organizationIds: commaSeparated(z.uuid()),
  // Narrow to the deals on this Objekt (IMM-1300), within the same visible set.
  propertyId: z.uuid().optional(),
});

export const ListWorkspaceDealsResponseSchema = ListDealsResponseSchema;

export type ListWorkspaceDealsPathParams = z.infer<typeof ListWorkspaceDealsPathParamsSchema>;
export type ListWorkspaceDealsQuery = z.infer<typeof ListWorkspaceDealsQuerySchema>;
export type ListWorkspaceDealsResponse = z.infer<typeof ListWorkspaceDealsResponseSchema>;

export const AdvanceDealPhasePathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

// The phase the deal should move into. Must be exactly the next phase in the
// pipeline; the handler refuses skips. Reaching ONLINE auto-wins the deal.
// `note` is an optional free-text account of the transition, stored on the
// forward phase-history row; empty / whitespace-only is treated as absent.
export const AdvanceDealPhaseBodySchema = z.object({
  toPhase: DealPhaseSchema,
  note: z.string().trim().max(1000).optional(),
});

export const AdvanceDealPhaseResponseSchema = DealResponseSchema;

export const RevertDealPhasePathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

// The earlier phase the deal should move back to. Must be strictly earlier than
// the current phase; skipping multiple phases backward in one move is allowed.
// The reason is mandatory — an empty / whitespace-only reason is refused.
export const RevertDealPhaseBodySchema = z.object({
  toPhase: DealPhaseSchema,
  reason: z.string().trim().min(1).max(1000),
});

export const RevertDealPhaseResponseSchema = DealResponseSchema;

export type CreateDealPathParams = z.infer<typeof CreateDealPathParamsSchema>;
export type CreateDealBody = z.infer<typeof CreateDealBodySchema>;
export type DealResponse = z.infer<typeof DealResponseSchema>;
export type CreateDealResponse = z.infer<typeof CreateDealResponseSchema>;
export type GetDealPathParams = z.infer<typeof GetDealPathParamsSchema>;
export type GetDealResponse = z.infer<typeof GetDealResponseSchema>;
export type AdvanceDealPhasePathParams = z.infer<typeof AdvanceDealPhasePathParamsSchema>;
export type AdvanceDealPhaseBody = z.infer<typeof AdvanceDealPhaseBodySchema>;
export type AdvanceDealPhaseResponse = z.infer<typeof AdvanceDealPhaseResponseSchema>;
export type RevertDealPhasePathParams = z.infer<typeof RevertDealPhasePathParamsSchema>;
export type RevertDealPhaseBody = z.infer<typeof RevertDealPhaseBodySchema>;
export type RevertDealPhaseResponse = z.infer<typeof RevertDealPhaseResponseSchema>;

// Phase-history reasons for one deal, keyed by the phase-history row id — the
// same id the deal-timeline activity carries as its `contentId`. The frontend
// merges these into the timeline so a forward note (and the backward `Grund`)
// renders under its transition. The reason is deliberately NOT carried on the
// audited `deal.phase-changed` event (ADR 0015) and never enters the activity
// store; it is read from the deal's own history table through this endpoint.
export const GetDealPhaseHistoryPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

export const DealPhaseHistoryItemSchema = z.object({
  id: z.uuid(),
  reason: z.string().nullable(),
});

export const GetDealPhaseHistoryResponseSchema = z.object({
  data: z.array(DealPhaseHistoryItemSchema),
});

export type GetDealPhaseHistoryPathParams = z.infer<typeof GetDealPhaseHistoryPathParamsSchema>;
export type DealPhaseHistoryItem = z.infer<typeof DealPhaseHistoryItemSchema>;
export type GetDealPhaseHistoryResponse = z.infer<typeof GetDealPhaseHistoryResponseSchema>;

// Who triggered a Phasen-Historie / status entry: a workspace member or the
// system (e.g. the deferred-until auto-resume). `actor_id` is null for SYSTEM.
// The deal timeline classifies its actor with the canonical Actor union
// (IMM-208). Deal rows only ever record MEMBER or SYSTEM, enforced by the
// per-table actor_type CHECK.
export const DealActorTypeSchema = ActorTypeSchema;
export type DealActorType = ActorType;

export const DEAL_CONTACT_CHANGE_TYPES = ['ADDED', 'REMOVED'] as const;
export const DealContactChangeTypeSchema = z.enum(DEAL_CONTACT_CHANGE_TYPES);
export type DealContactChangeType = z.infer<typeof DealContactChangeTypeSchema>;

// Add / remove an Eigentümer-Kontakt on an existing deal. Add takes the
// contact in the body; remove takes it in the path. Both return the updated
// deal so the caller can refresh the roster without a re-fetch.
export const AddDealContactPathParamsSchema = GetDealPathParamsSchema;

export const AddDealContactBodySchema = z.object({
  contactId: z.uuid(),
});

export const AddDealContactResponseSchema = DealResponseSchema;

export const RemoveDealContactPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
  contactId: z.uuid(),
});

export const RemoveDealContactResponseSchema = DealResponseSchema;

export type AddDealContactBody = z.infer<typeof AddDealContactBodySchema>;
export type AddDealContactResponse = z.infer<typeof AddDealContactResponseSchema>;
export type RemoveDealContactPathParams = z.infer<typeof RemoveDealContactPathParamsSchema>;
export type RemoveDealContactResponse = z.infer<typeof RemoveDealContactResponseSchema>;

// Inline-edit the deal's Notizen. Each provided field appends a new row to its
// `deal_obstacles` / `deal_next_steps` timeline — the latest row is what the
// read surfaces. Omitted fields are left unchanged; at least one is required.
// Refused on a won deal.
export const UpdateDealNotesPathParamsSchema = GetDealPathParamsSchema;

export const UpdateDealNotesBodySchema = z
  .object({
    obstacles: z.string().trim().max(1000).optional(),
    nextSteps: z.string().trim().max(1000).optional(),
  })
  .refine((body) => body.obstacles !== undefined || body.nextSteps !== undefined, {
    message: 'Provide at least one of obstacles or nextSteps',
  });

export const UpdateDealNotesResponseSchema = DealResponseSchema;

export type UpdateDealNotesBody = z.infer<typeof UpdateDealNotesBodySchema>;
export type UpdateDealNotesResponse = z.infer<typeof UpdateDealNotesResponseSchema>;

// Set the deal's Auftrag terms (IMM-1150). An omitted field is left unchanged; null clears it (BL-137:
// a caller that knows one term never fabricates the others). No `.refine`: an empty body is a harmless
// no-op, and a refined object could not be `.extend`ed into the MCP input.
export const UpdateDealMandateTermsPathParamsSchema = GetDealPathParamsSchema;

export const UpdateDealMandateTermsBodySchema = z.object({
  mandateType: MandateTypeSchema.nullable().optional(),
  mandateStartDate: z.iso.date().nullable().optional(),
  mandateDurationMonths: MandateDurationMonthsSchema.nullable().optional(),
  sellerCommissionPercent: MandateCommissionPercentSchema.nullable().optional(),
  // Deprecated (IMM-1380): accepted and ignored. The Objekt owns the buyer commission.
  buyerCommissionPercent: MandateCommissionPercentSchema.nullable().optional().meta({
    deprecated: true,
    description: DEPRECATED_BUYER_COMMISSION,
  }),
  // Unchanged rule from IMM-9's create body.
  expectedSalePrice: z.number().nonnegative().max(MAX_EXPECTED_SALE_PRICE).nullable().optional(),
});

export const UpdateDealMandateTermsResponseSchema = DealResponseSchema;

export type UpdateDealMandateTermsBody = z.infer<typeof UpdateDealMandateTermsBodySchema>;

// Put a deal on hold (`Zurückstellen`) or move its follow-up date. `deferredUntil`
// is required and must be today-or-later (workspace-local); the same endpoint
// re-targets the date when the deal is already deferred. Refused on a won / lost
// deal. Resume (`Wieder aufnehmen`) takes no body. Both return the updated deal.
export const DeferDealPathParamsSchema = GetDealPathParamsSchema;

export const DeferDealBodySchema = z.object({
  deferredUntil: z.iso.date(),
});

export const DeferDealResponseSchema = DealResponseSchema;

export const ResumeDealPathParamsSchema = GetDealPathParamsSchema;

export const ResumeDealResponseSchema = DealResponseSchema;

export type DeferDealBody = z.infer<typeof DeferDealBodySchema>;
export type DeferDealResponse = z.infer<typeof DeferDealResponseSchema>;
export type ResumeDealResponse = z.infer<typeof ResumeDealResponseSchema>;

// Mark a deal `Verloren` (`Als verloren markieren`). `lossReason` is required —
// exactly one of the thirteen fixed Verlustgrund values; the empty / omitted
// case is refused by the handler with a domain error, not by the schema, so the
// FE shows the "Bitte wähle einen Verlustgrund" copy. `lossNote` is an optional
// free-text Verlust-Notiz (0–1000 chars); it is recommended (but not enforced)
// when `lossReason` is OTHER. Only allowed from an interactive status; refused on
// a won deal. Returns the updated deal.
export const MarkDealLostPathParamsSchema = GetDealPathParamsSchema;

export const MarkDealLostBodySchema = z.object({
  lossReason: DealLossReasonSchema,
  lossNote: z.string().trim().max(1000).optional(),
});

export const MarkDealLostResponseSchema = DealResponseSchema;

// Reopen a `Verloren` deal (`Wieder öffnen`): status returns to `Aktiv`, the
// phase is restored to the value held before the loss, and the prior Verlustgrund
// / Verlust-Notiz are preserved in history but cleared off the live deal. Takes
// no body. Refused unless the deal is lost. Returns the updated deal.
export const ReopenDealPathParamsSchema = GetDealPathParamsSchema;

export const ReopenDealResponseSchema = DealResponseSchema;

export type MarkDealLostBody = z.infer<typeof MarkDealLostBodySchema>;
export type MarkDealLostResponse = z.infer<typeof MarkDealLostResponseSchema>;
export type ReopenDealResponse = z.infer<typeof ReopenDealResponseSchema>;

// Dismiss the post-Verloren cleanup banner (`[Behalten]`, IMM-171.3): stamps
// `cleanup_dismissed_at` = now so the banner stops nagging for this Verloren
// cycle. A no-op-safe setter — it carries no status guard; a reopen → re-lost
// bumps `lostAt` past the dismissal and re-offers the cleanup. Takes no body and
// returns the updated deal.
export const DismissDealPropertyCleanupPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

export const DismissDealPropertyCleanupResponseSchema = DealResponseSchema;

export type DismissDealPropertyCleanupPathParams = z.infer<typeof DismissDealPropertyCleanupPathParamsSchema>;
export type DismissDealPropertyCleanupResponse = z.infer<typeof DismissDealPropertyCleanupResponseSchema>;

// Record the member's pick on a Deal whose Auftrag carried its own Käuferprovision (IMM-1380.2): clears the stored value.
export const DismissBuyerCommissionDifferencePathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

export const DismissBuyerCommissionDifferenceBodySchema = z.object({}).strict();

export const DismissBuyerCommissionDifferenceResponseSchema = DealResponseSchema;

export type DismissBuyerCommissionDifferencePathParams = z.infer<
  typeof DismissBuyerCommissionDifferencePathParamsSchema
>;
export type DismissBuyerCommissionDifferenceBody = z.infer<typeof DismissBuyerCommissionDifferenceBodySchema>;
export type DismissBuyerCommissionDifferenceResponse = z.infer<typeof DismissBuyerCommissionDifferenceResponseSchema>;

// Soft-delete a deal (`Löschen`). Owner / Admin only (gated on the
// `organization:deal:deal-delete` permission). Refused on a won deal regardless of role —
// won deals are immutable. The deal is hidden from every list / get / filter
// response; there is no hard delete in V1 (restoration is tooling-only). Takes no
// body and returns 204 No Content.
export const DeleteDealPathParamsSchema = GetDealPathParamsSchema;

export const DeleteDealResponseSchema = DealResponseSchema;

export type DeleteDealPathParams = z.infer<typeof DeleteDealPathParamsSchema>;
export type DeleteDealResponse = z.infer<typeof DeleteDealResponseSchema>;

// Cross-Standort reassignment (IMM-20.4 AC22). Workspace-addressed:
// `POST /v1/workspaces/:workspaceId/deals/:id/standort`. The body carries the
// target organisation; the handler's authz rule decides whether the move is allowed.
export const ReassignDealStandortPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  id: z.uuid(),
});
export type ReassignDealStandortPathParams = z.infer<typeof ReassignDealStandortPathParamsSchema>;

export const ReassignDealStandortBodySchema = z
  .object({
    organizationId: z.uuid(),
  })
  .strict();
export type ReassignDealStandortBody = z.infer<typeof ReassignDealStandortBodySchema>;

export const ReassignDealStandortResponseSchema = DealResponseSchema;
export type ReassignDealStandortResponse = z.infer<typeof ReassignDealStandortResponseSchema>;

// How a member entered a deal's responsible set (IMM-402). The full enum is
// pinned now so later slices add emitters without a contract change; only
// `MANUAL` is written this slice (`LEAD_INHERITANCE` = qualification inheritance
// in IMM-402.2, `AUTO_CREATOR` = creation seeding in IMM-402.2). English
// UPPER_CASE code identifiers per AGENTS.md — the German PRD tokens (`manuell`,
// `lead_vererbung`, `erstellung`) are web-app copy, not code values.
// `AUTO_LOCATION_MANAGER` / `AUTO_OWNER_FALLBACK` (IMM-1104): an operator's
// Standort move left the deal with nobody responsible who sees the target
// Standort, so its Standortleitung — or the workspace Owner — took over, the same
// two sources a Lead's automatic assignment records.
export const DEAL_RESPONSIBILITY_SOURCES = [
  'MANUAL',
  'LEAD_INHERITANCE',
  'AUTO_CREATOR',
  'AUTO_LOCATION_MANAGER',
  'AUTO_OWNER_FALLBACK',
] as const;
export const DealResponsibilitySourceSchema = z.enum(DEAL_RESPONSIBILITY_SOURCES);
export type DealResponsibilitySource = z.infer<typeof DealResponsibilitySourceSchema>;

// Manual reassignment of a deal's responsible members (IMM-402.1). The set
// replaces the previous one; at least one member is required (400 when empty).
export const AssignDealResponsibleMembersPathParamsSchema = z.object({ workspaceId: z.uuid(), id: z.uuid() });
export const AssignDealResponsibleMembersBodySchema = z.object({
  memberIds: z.array(z.uuid()).min(1),
});
export type AssignDealResponsibleMembersBody = z.infer<typeof AssignDealResponsibleMembersBodySchema>;
export const AssignDealResponsibleMembersResponseSchema = DealResponseSchema;
export type AssignDealResponsibleMembersResponse = z.infer<typeof AssignDealResponsibleMembersResponseSchema>;

// The members the caller may assign a deal to (IMM-402.1): every workspace member
// for Owner/Workspace-Admin; only the caller's Standort subtree for a
// Standortleitung. Scoped server-side off the caller's visible organisations.
export const ListDealAssignableMembersPathParamsSchema = z.object({ workspaceId: z.uuid() });
export const DealAssignableMemberSchema = z.object({
  memberId: refersTo('member', z.uuid()),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string(),
});
export type DealAssignableMember = z.infer<typeof DealAssignableMemberSchema>;
export const ListDealAssignableMembersResponseSchema = z.object({ data: z.array(DealAssignableMemberSchema) });
export type ListDealAssignableMembersResponse = z.infer<typeof ListDealAssignableMembersResponseSchema>;
